import React, { useState, useEffect, useCallback, useRef } from 'react';
import logo    from './assests/logo.png';
import banner1 from './assests/banner1.png';
import banner2 from './assests/banner2.png';
import banner3 from './assests/banner3.png';

/* MUI Icons */
import VerifiedIcon          from '@mui/icons-material/Verified';
import HistoryEduIcon        from '@mui/icons-material/HistoryEdu';
import HandymanIcon          from '@mui/icons-material/Handyman';
import LocalShippingIcon     from '@mui/icons-material/LocalShipping';
import AutorenewIcon         from '@mui/icons-material/Autorenew';
import CurrencyExchangeIcon  from '@mui/icons-material/CurrencyExchange';
import PersonIcon            from '@mui/icons-material/Person';
import PhoneIcon             from '@mui/icons-material/Phone';
import EmailIcon             from '@mui/icons-material/Email';
import DiamondIcon           from '@mui/icons-material/Diamond';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import SendIcon              from '@mui/icons-material/Send';
import CheckCircleIcon       from '@mui/icons-material/CheckCircle';
import StorefrontIcon        from '@mui/icons-material/Storefront';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LinkIcon              from '@mui/icons-material/Link';
import LayersIcon            from '@mui/icons-material/Layers';
import TollIcon              from '@mui/icons-material/Toll';
import GrainIcon             from '@mui/icons-material/Grain';
import PaletteIcon           from '@mui/icons-material/Palette';
import AutoAwesomeIcon       from '@mui/icons-material/AutoAwesome';
import CloseIcon             from '@mui/icons-material/Close';
import WorkspacePremiumIcon  from '@mui/icons-material/WorkspacePremium';
import CelebrationIcon       from '@mui/icons-material/Celebration';
import StarIcon              from '@mui/icons-material/Star';

/* ─────────────────────────── inject fonts ─────────────────────────────── */
const injectFont = () => {
  const l = document.createElement('link');
  l.href  = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,700;0,900;1,400;1,700&family=Inter:wght@300;400;500;600;700&display=swap';
  l.rel   = 'stylesheet';
  document.head.appendChild(l);
};
injectFont();

/* ─────────────────────────── constants ────────────────────────────────── */
const G1 = '#D4AF37';   // gold main
const G2 = '#F5D068';   // gold light
const G3 = '#8B6914';   // gold dark
const W  = '#FFFFFF';
const BG = '#0A0A0F';   // near-black
const BG2= '#12121E';
const BG3= '#1C1C2E';

const serif = '"Playfair Display", Georgia, serif';
const sans  = '"Inter", system-ui, sans-serif';

const aG = (o) => `rgba(212,175,55,${o})`;
const aW = (o) => `rgba(255,255,255,${o})`;

/* ─────────────────────────── global styles ────────────────────────────── */
const GlobalStyle = () => (
  <style>{`
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body { background: ${BG}; color: #e8e0d0; font-family: ${sans}; overflow-x: hidden; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: #111; }
    ::-webkit-scrollbar-thumb { background: linear-gradient(${G3}, ${G1}); border-radius: 4px; }
    ::selection { background: ${aG(0.3)}; color: #fff; }

    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes float {
      0%,100% { transform: translateY(0px); }
      50%      { transform: translateY(-12px); }
    }
    @keyframes pulse-ring {
      0%   { transform: scale(0.9); opacity: 0.8; }
      100% { transform: scale(1.3); opacity: 0; }
    }
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(30px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes ticker {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    @keyframes spin-slow {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes bounce {
      0%,100% { transform: translateY(0); }
      50%      { transform: translateY(8px); }
    }
    @keyframes glow-pulse {
      0%,100% { box-shadow: 0 0 20px ${aG(0.3)}, 0 0 40px ${aG(0.1)}; }
      50%      { box-shadow: 0 0 40px ${aG(0.6)}, 0 0 80px ${aG(0.2)}; }
    }
    @keyframes border-spin {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    .fade-up { animation: fadeUp 0.8s ease forwards; }

    /* Glass card */
    .glass {
      background: linear-gradient(135deg, ${aW(0.06)} 0%, ${aW(0.02)} 100%);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid ${aW(0.1)};
    }
    .glass-gold {
      background: linear-gradient(135deg, ${aG(0.12)} 0%, ${aG(0.04)} 100%);
      backdrop-filter: blur(20px);
      border: 1px solid ${aG(0.25)};
    }

    /* Gold shimmer text */
    .gold-shimmer {
      background: linear-gradient(90deg, ${G3} 0%, ${G1} 25%, ${G2} 50%, ${G1} 75%, ${G3} 100%);
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: shimmer 3s linear infinite;
    }
    .gold-text {
      background: linear-gradient(135deg, ${G2} 0%, ${G1} 60%, ${G3} 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    /* Hover lift */
    .lift {
      transition: transform 0.35s cubic-bezier(0.4,0,0.2,1), box-shadow 0.35s;
    }
    .lift:hover {
      transform: translateY(-8px);
      box-shadow: 0 32px 64px rgba(0,0,0,0.5), 0 0 60px ${aG(0.12)};
    }

    /* Nav link */
    .nav-link {
      color: ${aW(0.7)};
      text-decoration: none;
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      transition: color 0.25s;
      position: relative;
      padding-bottom: 2px;
    }
    .nav-link::after {
      content: '';
      position: absolute;
      bottom: -2px; left: 0;
      width: 0; height: 1px;
      background: ${G1};
      transition: width 0.3s;
    }
    .nav-link:hover { color: ${G1}; }
    .nav-link:hover::after { width: 100%; }

    /* Gold button */
    .btn-gold {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 14px 36px;
      background: linear-gradient(135deg, ${G2} 0%, ${G1} 50%, ${G3} 100%);
      color: #0A0A0F;
      font-family: ${sans};
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      text-decoration: none;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s;
      position: relative;
      overflow: hidden;
    }
    .btn-gold::before {
      content: '';
      position: absolute;
      top: 0; left: -100%;
      width: 100%; height: 100%;
      background: linear-gradient(90deg, transparent, ${aW(0.2)}, transparent);
      transition: left 0.5s;
    }
    .btn-gold:hover::before { left: 100%; }
    .btn-gold:hover { transform: translateY(-2px); box-shadow: 0 12px 32px ${aG(0.45)}; }

    /* Outline gold button */
    .btn-outline {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 13px 34px;
      background: transparent;
      color: ${G1};
      font-family: ${sans};
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      text-decoration: none;
      border: 1.5px solid ${aG(0.5)};
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s;
    }
    .btn-outline:hover {
      background: ${aG(0.08)};
      border-color: ${G1};
      color: ${G2};
      transform: translateY(-2px);
    }

    /* Section divider ornament */
    .ornament {
      display: flex; align-items: center; justify-content: center; gap: 12px;
      margin-bottom: 16px;
    }
    .ornament-line { height: 1px; width: 60px; background: linear-gradient(90deg, transparent, ${G1}); }
    .ornament-line.rev { background: linear-gradient(90deg, ${G1}, transparent); }

    /* Collection card */
    .coll-card {
      position: relative;
      border-radius: 3px;
      overflow: hidden;
      background: ${BG2};
      border: 1px solid ${aG(0.1)};
      transition: all 0.35s cubic-bezier(0.4,0,0.2,1);
      cursor: pointer;
    }
    .coll-card::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, ${aG(0.08)}, transparent);
      opacity: 0;
      transition: opacity 0.35s;
      z-index: 1;
      pointer-events: none;
    }
    .coll-card:hover { transform: translateY(-6px); border-color: ${aG(0.4)}; box-shadow: 0 24px 60px rgba(0,0,0,0.6), 0 0 40px ${aG(0.1)}; }
    .coll-card:hover::before { opacity: 1; }
  `}</style>
);

/* ─────────────────────────── helpers ──────────────────────────────────── */
const px = (s) => ({ xs: s * 0.55, md: s });  // responsive font

/* ─────────────────────────── data ─────────────────────────────────────── */
const SLIDES = [
  { img: banner1, eyebrow: 'New Collection · 2026', h1: 'Crafting', h2: 'Timeless', h3: 'Jewellery', sub: 'Handcrafted gold, diamond & silver jewellery from Bengaluru\'s oldest market — since 1985.' },
  { img: banner2, eyebrow: 'Bridal Collection',     h1: 'For Your',  h2: 'Most Special', h3: 'Moments', sub: 'Exquisite bridal sets crafted by master karigar artisans for your perfect day.' },
  { img: banner3, eyebrow: 'Diamond & Gold',        h1: 'Brilliance', h2: 'In Every',  h3: 'Facet',   sub: 'IGI-certified diamonds. BIS hallmarked gold. Pure, trusted, beautiful.' },
];

const COLLECTIONS = [
  { name: 'Gold Chains',       iconKey: 'link',      color: G1,         desc: '22KT & 18KT handcrafted in 100+ traditional and contemporary designs.', tag: 'Bestseller' },
  { name: 'Necklace Sets',     iconKey: 'layers',    color: '#E8A87C',  desc: 'Temple, kundan & stone sets for weddings and grand celebrations.',       tag: 'Bridal' },
  { name: 'Bangles & Kadas',   iconKey: 'toll',      color: G2,         desc: 'Plain gold bangles, studded kadas and delicate bracelets.',               tag: 'Classic' },
  { name: 'Diamond Jewellery', iconKey: 'diamond',   color: '#7EB8F7',  desc: 'IGI-certified rings, pendants and earrings of exceptional brilliance.',   tag: 'IGI Certified' },
  { name: 'Silver Articles',   iconKey: 'grain',     color: '#B0C4DE',  desc: 'Sterling silver payal, anklets, pooja articles and silverware.',          tag: 'Pure Silver' },
  { name: 'Custom Orders',     iconKey: 'palette',   color: '#98D8A0',  desc: 'Bring your dream design to life — crafted exclusively for you.',          tag: 'Made for You' },
];

const COLL_ICONS = {
  link:    <LinkIcon     sx={{ fontSize: 28 }} />,
  layers:  <LayersIcon   sx={{ fontSize: 28 }} />,
  toll:    <TollIcon     sx={{ fontSize: 28 }} />,
  diamond: <DiamondIcon  sx={{ fontSize: 28 }} />,
  grain:   <GrainIcon    sx={{ fontSize: 28 }} />,
  palette: <PaletteIcon  sx={{ fontSize: 28 }} />,
};

const WHY = [
  { n: '01', t: 'BIS Hallmarked',      d: 'Every gold piece is certified. Purity guaranteed, no exceptions, no compromises.',      iconKey: 'verified' },
  { n: '02', t: '40+ Years of Trust',  d: 'Family-run since 1985 in Nagarathpete — three generations of craftsmanship.',           iconKey: 'history' },
  { n: '03', t: 'Master Craftsmen',    d: 'Skilled karigar teams handcraft every piece with old-world precision.',                   iconKey: 'handyman' },
  { n: '04', t: 'Insured Delivery',    d: 'Secure, fully insured delivery across Bengaluru and all major Indian cities.',           iconKey: 'shipping' },
  { n: '05', t: 'Lifetime Service',    d: 'Free cleaning, polishing and repairs on every Pushpa Chains purchase, forever.',         iconKey: 'autorenew' },
  { n: '06', t: 'Best Exchange Rates', d: 'Unmatched exchange value for old gold, silver and diamonds.',                            iconKey: 'exchange' },
];

const WHY_ICONS = {
  verified:  <VerifiedIcon        sx={{ fontSize: 30, color: G1 }} />,
  history:   <HistoryEduIcon      sx={{ fontSize: 30, color: G1 }} />,
  handyman:  <HandymanIcon        sx={{ fontSize: 30, color: G1 }} />,
  shipping:  <LocalShippingIcon   sx={{ fontSize: 30, color: G1 }} />,
  autorenew: <AutorenewIcon       sx={{ fontSize: 30, color: G1 }} />,
  exchange:  <CurrencyExchangeIcon sx={{ fontSize: 30, color: G1 }} />,
};

const REVIEWS = [
  { n: 'Meena R.',  l: 'Jayanagar',    t: 'My bridal necklace was the showstopper of my wedding. Every guest asked where I got it. Pushpa Chains is a treasure of Bengaluru.' },
  { n: 'Anand K.',  l: 'Malleshwaram', t: 'Buying gold chains here for 15 years. Transparent pricing, BIS certification, honest service. No place compares.' },
  { n: 'Priya S.',  l: 'Koramangala',  t: 'Got a custom diamond pendant made — exactly as I envisioned. Flawless craftsmanship, delivered on time, perfect price.' },
  { n: 'Suresh M.', l: 'RT Nagar',     t: 'Best exchange rates for old gold in Bengaluru, period. The team treats you like family. I never go anywhere else.' },
];

/* ─────────────────────────── components ───────────────────────────────── */

/* Ornament divider */
const Ornament = ({ light }) => (
  <div className="ornament">
    <div className="ornament-line" style={{ background: `linear-gradient(90deg, transparent, ${light ? aW(0.4) : G1})` }} />
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 0L10.2 7.8L18 9L10.2 10.2L9 18L7.8 10.2L0 9L7.8 7.8Z" fill={G1} />
    </svg>
    <div className="ornament-line rev" style={{ background: `linear-gradient(90deg, ${light ? aW(0.4) : G1}, transparent)` }} />
  </div>
);

/* Section heading — last word always gets gold shimmer */
const SH = ({ eyebrow, title, sub, light }) => {
  // Split title into words, last word gets gold
  const words = typeof title === 'string' ? title.split(' ') : null;
  const rendered = words
    ? words.map((w, i) => i === words.length - 1
        ? <em key={i} className="gold-shimmer" style={{ fontStyle: 'normal' }}>{w}</em>
        : <span key={i}>{w} </span>)
    : title;

  return (
    <div style={{ textAlign: 'center', marginBottom: 64 }}>
      <Ornament light={light} />
      {eyebrow && (
        <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: G1, marginBottom: 14 }}>
          {eyebrow}
        </p>
      )}
      <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 4vw, 3.6rem)', color: W, lineHeight: 1.1, marginBottom: sub ? 20 : 0, fontWeight: 700 }}>
        {rendered}
      </h2>
      {sub && (
        <p style={{ fontSize: 16.5, color: aW(0.52), maxWidth: 530, margin: '0 auto', lineHeight: 1.88 }}>
          {sub}
        </p>
      )}
    </div>
  );
};

/* ─────────────────────────── Navbar ────────────────────────────────────── */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mob, setMob]           = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const navStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
    padding: '0 40px',
    display: 'flex', alignItems: 'center', height: 76,
    background: scrolled ? 'rgba(10,10,15,0.95)' : 'transparent',
    backdropFilter: scrolled ? 'blur(24px)' : 'none',
    borderBottom: scrolled ? `1px solid ${aG(0.15)}` : 'none',
    transition: 'all 0.4s ease',
  };

  return (
    <>
      <nav style={navStyle}>
        {/* Logo — bigger circular emblem + brand name */}
        <a href="#hero" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none' }}>
          <div style={{
            width: scrolled ? 52 : 58, height: scrolled ? 52 : 58,
            borderRadius: '10%', overflow: 'hidden', flexShrink: 0,
            border: `1.5px solid ${aG(scrolled ? 0.5 : 0.7)}`,
            boxShadow: `0 0 ${scrolled ? 16 : 28}px ${aG(scrolled ? 0.3 : 0.45)}`,
            background: BG,
            transition: 'all 0.35s ease',
          }}>
            <img src={logo} alt="Pushpa Chains" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div>
            <div style={{ fontFamily: serif, fontSize: scrolled ? 19 : 21, fontWeight: 700, lineHeight: 1.6, transition: 'font-size 0.35s' }} className="gold-text">
              Pushpa Chains
            </div>
            <div style={{ fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: aW(scrolled ? 0.35 : 0.5), lineHeight: 1, transition: 'all 0.35s' }}>
              Fine Jewellery · Est. 1985
            </div>
          </div>
        </a>

        {/* Desktop links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 36 }} className="desktop-nav">
          {[['Collections','#collections'],['About','#about'],['Why Us','#why'],['Reviews','#reviews'],['Contact','#contact']].map(([l, h]) => (
            <a key={l} href={h} className="nav-link">{l}</a>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginLeft: 40 }}>
          <a href="#contact" className="btn-gold" style={{ padding: '10px 24px', fontSize: 12 }}>Visit Store</a>
          <button onClick={() => setMob(true)} style={{ background: 'none', border: `1px solid ${aG(0.4)}`, borderRadius: 4, padding: '8px 10px', cursor: 'pointer', color: G1, display: 'none' }} className="mob-btn">
            ☰
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mob && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex' }}>
          <div onClick={() => setMob(false)} style={{ flex: 1, background: 'rgba(0,0,0,0.7)' }} />
          <div style={{ width: 280, background: BG2, borderLeft: `1px solid ${aG(0.15)}`, padding: 32, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', overflow: 'hidden', border: `2px solid ${aG(0.4)}`, flexShrink: 0 }}>
                  <img src={logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div>
                  <div style={{ fontFamily: serif, fontSize: 15, fontWeight: 700, lineHeight: 1 }} className="gold-text">Pushpa Chains</div>
                  <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: aW(0.35) }}>Fine Jewellery</div>
                </div>
              </div>
              <button onClick={() => setMob(false)} style={{ background: 'none', border: 'none', color: aW(0.6), fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>
            {[['Collections','#collections'],['About','#about'],['Why Us','#why'],['Reviews','#reviews'],['Contact','#contact']].map(([l, h]) => (
              <a key={l} href={h} onClick={() => setMob(false)} style={{ padding: '14px 0', borderBottom: `1px solid ${aW(0.06)}`, color: aW(0.8), textDecoration: 'none', fontSize: 15, fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{l}</a>
            ))}
            <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <a href="#contact" onClick={() => setMob(false)} className="btn-gold" style={{ justifyContent: 'center' }}>Visit Store</a>
              <a href="https://wa.me/917349341733" target="_blank" rel="noreferrer" className="btn-outline" style={{ justifyContent: 'center' }}>WhatsApp</a>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mob-btn { display: flex !important; }
        }
        @media (max-width: 640px) {
          #hero > div[style*="paddingLeft"] > div {
            max-width: 100% !important;
          }
        }
      `}</style>
    </>
  );
}

/* ─────────────────────────── Hero ──────────────────────────────────────── */
function Hero() {
  const [idx, setIdx]   = useState(0);
  const [fading, setFading] = useState(false);
  const total = SLIDES.length;

  const goTo = useCallback((n) => {
    setFading(true);
    setTimeout(() => { setIdx((n + total) % total); setFading(false); }, 400);
  }, [total]);

  useEffect(() => {
    const t = setInterval(() => goTo(idx + 1), 6000);
    return () => clearInterval(t);
  }, [idx, goTo]);

  const s = SLIDES[idx];

  return (
    <div id="hero" style={{ position: 'relative', height: '100dvh', minHeight: 680, overflow: 'hidden' }}>
      {/* All 3 banner images — full bleed, no clipping */}
      {SLIDES.map((sl, i) => (
        <div key={i} style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${sl.img})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          opacity: i === idx && !fading ? 1 : 0,
          transition: 'opacity 1s ease',
          zIndex: 0,
        }} />
      ))}

      {/* Gradient — ONLY heavy on left, clears by 45% so banner image shows */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1,
        background: 'linear-gradient(95deg, rgba(8,8,14,0.95) 0%, rgba(8,8,14,0.85) 28%, rgba(8,8,14,0.45) 44%, rgba(8,8,14,0.08) 58%, transparent 72%)'
      }} />
      {/* Bottom fade so stats are readable */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', zIndex: 1,
        background: 'linear-gradient(0deg, rgba(8,8,14,0.75) 0%, transparent 100%)'
      }} />
      {/* Top fade so navbar is clear */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '20%', zIndex: 1,
        background: 'linear-gradient(180deg, rgba(8,8,14,0.5) 0%, transparent 100%)'
      }} />

      {/* Subtle gold glow top-left */}
      <div style={{ position: 'absolute', top: '15%', left: '-5%', width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, ${aG(0.07)} 0%, transparent 65%)`, zIndex: 1, pointerEvents: 'none' }} />

      {/* Content — strictly left-aligned, max 48% width */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center',
        paddingLeft: 'clamp(28px, 6.5vw, 110px)', paddingRight: 'clamp(28px, 4vw, 60px)',
        paddingTop: 80,  // clear navbar
      }}>
        <div style={{ maxWidth: 'min(500px, 46vw)', width: '100%',
          opacity: fading ? 0 : 1,
          transform: fading ? 'translateY(18px)' : 'translateY(0)',
          transition: 'all 0.55s ease',
        }}>
          {/* Eyebrow tag */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '7px 18px',
            background: aG(0.12), border: `1px solid ${aG(0.35)}`, borderRadius: 40,
            backdropFilter: 'blur(12px)', marginBottom: 26 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: G1, display: 'block', animation: 'glow-pulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: G1 }}>{s.eyebrow}</span>
          </div>

          {/* Headline — 3 lines */}
          <h1 style={{ fontFamily: serif, lineHeight: 1.02, marginBottom: 22, color: W }}>
            <span style={{ display: 'block', fontSize: 'clamp(2.4rem, 5.5vw, 5.8rem)', fontWeight: 400 }}>{s.h1}</span>
            <span className="gold-shimmer" style={{ display: 'block', fontSize: 'clamp(2.8rem, 6.5vw, 6.8rem)', fontWeight: 900, fontStyle: 'italic', lineHeight: 1.0 }}>{s.h2}</span>
            <span style={{ display: 'block', fontSize: 'clamp(2.4rem, 5.5vw, 5.8rem)', fontWeight: 400 }}>{s.h3}</span>
          </h1>

          <p style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', color: aW(0.68), lineHeight: 1.85, marginBottom: 40, maxWidth: 440 }}>
            {s.sub}
          </p>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 56 }}>
            <a href="#collections" className="btn-gold" style={{ padding: '13px 30px', fontSize: 13 }}>
              Explore Collections
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
            <a href="https://wa.me/917349341733" target="_blank" rel="noreferrer" className="btn-outline" style={{ padding: '12px 26px', fontSize: 13 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
              WhatsApp
            </a>
          </div>

          {/* Stats row — 4 clean numbers */}
          <div style={{ display: 'flex', gap: 'clamp(20px, 3vw, 40px)', flexWrap: 'wrap' }}>
            {[['40+', 'Years'], ['10K+', 'Families'], ['BIS', 'Certified'], ['3rd Gen', 'Heritage']].map(([v, l]) => (
              <div key={l}>
                <div style={{ fontFamily: serif, fontSize: 'clamp(1.6rem, 2.8vw, 2.4rem)', fontWeight: 900, lineHeight: 1 }} className="gold-text">{v}</div>
                <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: aW(0.38), marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Slide navigation */}
      {[-1, 1].map((dir) => (
        <button key={dir} onClick={() => goTo(idx + dir)} style={{
          position: 'absolute', zIndex: 3, top: '50%', transform: 'translateY(-50%)',
          [dir === -1 ? 'left' : 'right']: 28,
          width: 50, height: 50, borderRadius: '50%',
          background: aW(0.06), border: `1px solid ${aW(0.15)}`,
          color: W, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(12px)', transition: 'all 0.3s',
          fontSize: 18,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = aG(0.3); e.currentTarget.style.borderColor = G1; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = aW(0.06); e.currentTarget.style.borderColor = aW(0.15); }}>
          {dir === -1 ? '‹' : '›'}
        </button>
      ))}

      {/* Dots */}
      <div style={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', zIndex: 3, display: 'flex', gap: 10 }}>
        {SLIDES.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} style={{
            width: i === idx ? 32 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer',
            background: i === idx ? G1 : aW(0.35),
            transition: 'all 0.35s', padding: 0,
          }} />
        ))}
      </div>

      {/* Scroll hint */}
      <div style={{ position: 'absolute', bottom: 36, right: 48, zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: aW(0.3), writingMode: 'vertical-rl' }}>SCROLL</span>
        <div style={{ width: 1, height: 48, background: `linear-gradient(${aW(0.3)}, transparent)` }} />
      </div>
    </div>
  );
}

/* ─────────────────────────── Ticker ────────────────────────────────────── */
function Ticker() {
  const items = ['✦ BIS Hallmarked Gold', '✦ 40+ Years of Trust', '✦ Master Craftsmen', '✦ Open Until 10 PM', '✦ Free Lifetime Service', '✦ Custom Orders Welcome', '✦ Best Exchange Rates', '✦ IGI Certified Diamonds'];
  const rep = [...items, ...items];
  return (
    <div style={{ background: `linear-gradient(90deg, ${G3}, ${G1}, ${G2}, ${G1}, ${G3})`, overflow: 'hidden', padding: '10px 0', position: 'relative' }}>
      <div style={{ display: 'flex', animation: 'ticker 25s linear infinite', whiteSpace: 'nowrap' }}>
        {rep.map((t, i) => (
          <span key={i} style={{ fontSize: 12, fontWeight: 700, color: BG, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0 28px', flexShrink: 0 }}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── Collections ───────────────────────────────── */
function Collections() {
  const [hovered, setHovered] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCollection, setModalCollection] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [focusField, setFocusField] = useState(null);
  const [sent, setSent] = useState(false);

  const openModal = (c) => { setModalCollection(c); setModalOpen(true); setSent(false); setForm({ name: '', phone: '', message: '' }); };
  const closeModal = () => { setModalOpen(false); setModalCollection(null); };

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = encodeURIComponent(`Hi Pushpa Chains! I'm interested in: ${modalCollection?.name}\n\nName: ${form.name}\nPhone: ${form.phone}${form.message ? '\nMessage: ' + form.message : ''}`);
    window.open(`https://wa.me/917349341733?text=${msg}`, '_blank');
    setSent(true);
    setTimeout(() => { setSent(false); closeModal(); }, 3500);
  };

  const iStyle = (f) => ({
    width: '100%', padding: '13px 16px 13px 46px',
    background: focusField === f ? aG(0.06) : BG2,
    border: `1.5px solid ${focusField === f ? G1 : aG(0.2)}`,
    borderRadius: 10, color: W, fontSize: 14, fontFamily: sans, outline: 'none',
    transition: 'all 0.25s', boxShadow: focusField === f ? `0 0 0 3px ${aG(0.1)}` : 'none',
  });

  return (
    <section id="collections" style={{ padding: 'clamp(60px,8vw,120px) clamp(20px,6vw,100px)', background: BG2, position: 'relative', overflow: 'hidden' }}>

      {/* Decorative background glow */}
      <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%,-50%)', width: 700, height: 700, borderRadius: '50%', background: `radial-gradient(circle, ${aG(0.04)} 0%, transparent 65%)`, pointerEvents: 'none' }} />

      <SH eyebrow="Our Collections" title="Jewellery for Every Occasion"
        sub="From everyday gold chains to exquisite bridal sets — each piece handcrafted with four decades of passion." />

      {/* ── Featured banner card (full width top) ── */}
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>

        {/* Top featured strip */}
        <div style={{
          position: 'relative', borderRadius: 16, overflow: 'hidden', marginBottom: 20,
          height: 'clamp(160px,22vw,260px)',
          background: `linear-gradient(120deg, ${aG(0.18)} 0%, ${BG} 50%, ${aG(0.06)} 100%)`,
          border: `1px solid ${aG(0.2)}`,
          boxShadow: `0 8px 40px rgba(0,0,0,0.4), 0 0 60px ${aG(0.08)}`,
          display: 'flex', alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => openModal(COLLECTIONS[0])}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = aG(0.5); e.currentTarget.style.boxShadow = `0 20px 60px rgba(0,0,0,0.5), 0 0 80px ${aG(0.15)}`; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = aG(0.2); e.currentTarget.style.boxShadow = `0 8px 40px rgba(0,0,0,0.4), 0 0 60px ${aG(0.08)}`; }}>

          {/* Left gold accent */}
          <div style={{ width: 5, alignSelf: 'stretch', background: `linear-gradient(180deg, ${G2}, ${G1}, ${G3})`, flexShrink: 0 }} />

          <div style={{ padding: 'clamp(24px,4vw,48px)', flex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', background: aG(0.2), border: `1px solid ${aG(0.4)}`, borderRadius: 30, marginBottom: 16 }}>
              <StarIcon sx={{ fontSize: 13, color: G1 }} />
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: G1 }}>Bestseller · Most Popular</span>
            </div>
            <h3 style={{ fontFamily: serif, fontSize: 'clamp(22px,3.5vw,40px)', fontWeight: 900, lineHeight: 1.1, marginBottom: 10 }} className="gold-shimmer">
              Gold Chains Collection
            </h3>
            <p style={{ fontSize: 'clamp(13px,1.4vw,16px)', color: aW(0.55), lineHeight: 1.75, maxWidth: 560, marginBottom: 20 }}>
              22KT &amp; 18KT handcrafted in 100+ traditional and contemporary designs. BIS hallmarked, sourced from Bengaluru's finest karigar workshops.
            </p>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 24px', background: `linear-gradient(135deg, ${G2}, ${G1})`, borderRadius: 8, color: BG, fontWeight: 800, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' }}>
              <AutoAwesomeIcon sx={{ fontSize: 16 }} />
              Enquire Now
            </div>
          </div>

          {/* Right decorative */}
          <div style={{ width: 'clamp(120px,18vw,220px)', alignSelf: 'stretch', background: `linear-gradient(135deg, ${aG(0.12)}, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <div style={{ width: 90, height: 90, borderRadius: '50%', background: `radial-gradient(circle, ${aG(0.25)}, ${aG(0.05)})`, border: `2px solid ${aG(0.35)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 40px ${aG(0.2)}` }}>
              <LinkIcon sx={{ fontSize: 40, color: G1 }} />
            </div>
          </div>
        </div>

        {/* ── Bottom 5 cards in a responsive grid ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }} className="coll-bottom-grid">
          {COLLECTIONS.slice(1).map((c, i) => {
            const idx = i + 1;
            const isHov = hovered === idx;
            return (
              <div key={c.name}
                onMouseEnter={() => setHovered(idx)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => openModal(c)}
                style={{
                  position: 'relative', overflow: 'hidden',
                  background: isHov ? `linear-gradient(145deg, ${c.color}16 0%, ${BG} 100%)` : BG,
                  border: `1px solid ${isHov ? c.color + '60' : aW(0.07)}`,
                  borderRadius: 14, padding: '28px 24px 24px',
                  transition: 'all 0.32s cubic-bezier(0.4,0,0.2,1)',
                  transform: isHov ? 'translateY(-7px)' : 'none',
                  boxShadow: isHov ? `0 24px 56px rgba(0,0,0,0.5), 0 0 36px ${c.color}22` : '0 2px 16px rgba(0,0,0,0.25)',
                  cursor: 'pointer',
                }}>

                {/* Top glow line */}
                <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: 2, background: isHov ? `linear-gradient(90deg, transparent, ${c.color}, transparent)` : 'transparent', transition: 'all 0.35s', borderRadius: 2 }} />

                {/* Tag badge */}
                <div style={{
                  position: 'absolute', top: 14, right: 14,
                  padding: '3px 10px', borderRadius: 20,
                  background: `${c.color}20`, border: `1px solid ${c.color}40`,
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: c.color,
                }}>{c.tag}</div>

                {/* Icon */}
                <div style={{
                  width: 58, height: 58, borderRadius: 14, marginBottom: 20,
                  background: `linear-gradient(135deg, ${c.color}20, ${c.color}06)`,
                  border: `1.5px solid ${isHov ? c.color + '70' : c.color + '30'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: c.color,
                  boxShadow: isHov ? `0 0 24px ${c.color}30` : 'none',
                  transition: 'all 0.35s',
                }}>
                  {COLL_ICONS[c.iconKey]}
                </div>

                <h3 style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, color: isHov ? W : aW(0.9), marginBottom: 8, lineHeight: 1.25, transition: 'color 0.3s' }}>
                  {c.name}
                </h3>
                <p style={{ fontSize: 13, color: aW(isHov ? 0.6 : 0.42), lineHeight: 1.75, marginBottom: 18, transition: 'color 0.3s' }}>
                  {c.desc}
                </p>

                {/* CTA row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isHov ? c.color : aW(0.28), transition: 'color 0.3s' }}>
                  Enquire
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ transform: isHov ? 'translateX(3px)' : 'none', transition: 'transform 0.3s' }}>
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA below grid */}
        <div style={{ textAlign: 'center', marginTop: 52 }}>
          <p style={{ fontSize: 15, color: aW(0.38), marginBottom: 20 }}>
            Looking for something specific? We craft custom jewellery too.
          </p>
          <button onClick={() => openModal(COLLECTIONS[5])} className="btn-gold" style={{ border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            Enquire on WhatsApp
          </button>
        </div>
      </div>

      {/* ── Collection Enquiry Modal ── */}
      {modalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          {/* Backdrop */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,10,0.88)', backdropFilter: 'blur(16px)' }} onClick={closeModal} />

          {/* Modal card */}
          <div style={{
            position: 'relative', zIndex: 1, width: '100%', maxWidth: 520,
            background: BG3, borderRadius: 20,
            border: `1px solid ${modalCollection ? modalCollection.color + '40' : aG(0.25)}`,
            boxShadow: `0 48px 120px rgba(0,0,0,0.8), 0 0 80px ${modalCollection ? modalCollection.color + '18' : aG(0.1)}`,
            overflow: 'hidden',
          }}>

            {/* Top color accent bar */}
            <div style={{ height: 4, background: modalCollection ? `linear-gradient(90deg, transparent, ${modalCollection.color}, transparent)` : `linear-gradient(90deg, transparent, ${G1}, transparent)` }} />

            {/* Header */}
            <div style={{ padding: '28px 32px 20px', display: 'flex', alignItems: 'flex-start', gap: 16, borderBottom: `1px solid ${aW(0.07)}` }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14, flexShrink: 0,
                background: modalCollection ? `${modalCollection.color}20` : aG(0.15),
                border: `1.5px solid ${modalCollection ? modalCollection.color + '50' : aG(0.4)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: modalCollection?.color || G1,
              }}>
                {modalCollection && COLL_ICONS[modalCollection.iconKey]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: modalCollection?.color || G1, marginBottom: 4 }}>Enquiry For</div>
                <h3 style={{ fontFamily: serif, fontSize: 22, fontWeight: 700, color: W, lineHeight: 1.2 }}>{modalCollection?.name}</h3>
                <p style={{ fontSize: 13, color: aW(0.45), marginTop: 4, lineHeight: 1.6 }}>{modalCollection?.desc}</p>
              </div>
              <button onClick={closeModal} style={{ background: aW(0.07), border: `1px solid ${aW(0.1)}`, borderRadius: 8, padding: 6, cursor: 'pointer', color: aW(0.5), display: 'flex', flexShrink: 0, transition: 'all 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = aW(0.12); e.currentTarget.style.color = W; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = aW(0.07); e.currentTarget.style.color = aW(0.5); }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px 32px 32px' }}>
              {sent ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: aG(0.15), border: `2px solid ${aG(0.5)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', animation: 'glow-pulse 2s ease-in-out infinite' }}>
                    <CheckCircleIcon sx={{ fontSize: 36, color: G1 }} />
                  </div>
                  <h4 style={{ fontFamily: serif, fontSize: 22, color: W, marginBottom: 8 }}>Sent <em className="gold-text">Successfully!</em></h4>
                  <p style={{ fontSize: 14, color: aW(0.5), lineHeight: 1.7 }}>Redirecting you to WhatsApp. We'll reply shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: G1, marginBottom: 7 }}>Your Name *</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: aW(0.3), display: 'flex', pointerEvents: 'none' }}><PersonIcon sx={{ fontSize: 18 }} /></div>
                      <input type="text" required placeholder="Full name" value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        onFocus={() => setFocusField('name')} onBlur={() => setFocusField(null)}
                        style={iStyle('name')} />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: G1, marginBottom: 7 }}>Phone Number *</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: aW(0.3), display: 'flex', pointerEvents: 'none' }}><PhoneIcon sx={{ fontSize: 18 }} /></div>
                      <input type="tel" required placeholder="+91 XXXXX XXXXX" value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        onFocus={() => setFocusField('phone')} onBlur={() => setFocusField(null)}
                        style={iStyle('phone')} />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: G1, marginBottom: 7 }}>Message <span style={{ color: aW(0.3), fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: 13, top: 14, color: aW(0.3), display: 'flex', pointerEvents: 'none' }}><ChatBubbleOutlineIcon sx={{ fontSize: 18 }} /></div>
                      <textarea rows={3} placeholder="Budget, occasion, specific design..." value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        onFocus={() => setFocusField('msg')} onBlur={() => setFocusField(null)}
                        style={{ ...iStyle('msg'), resize: 'none', paddingTop: 13, lineHeight: 1.6 }} />
                    </div>
                  </div>
                  <button type="submit" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    padding: '15px', marginTop: 4,
                    background: `linear-gradient(135deg, ${G2}, ${G1}, ${G3})`,
                    color: BG, border: 'none', borderRadius: 10,
                    fontFamily: sans, fontSize: 14, fontWeight: 800,
                    letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                    boxShadow: `0 8px 28px ${aG(0.4)}`, transition: 'all 0.3s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 16px 40px ${aG(0.55)}`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 8px 28px ${aG(0.4)}`; }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                    Send via WhatsApp
                    <SendIcon sx={{ fontSize: 17 }} />
                  </button>
                  <p style={{ fontSize: 11, color: aW(0.28), textAlign: 'center' }}>You'll be redirected to WhatsApp to complete your enquiry.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes shimmer-sweep {
          from { left: -100%; }
          to   { left: 150%; }
        }
        @media (max-width: 640px) {
          .coll-bottom-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 380px) {
          .coll-bottom-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}

/* ─────────────────────────── Banner strip 2 ────────────────────────────── */
function BannerMid() {
  return (
    <div style={{ position: 'relative', height: 'clamp(300px, 40vw, 520px)', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${banner2})`, backgroundSize: 'cover', backgroundPosition: 'center 30%', transform: 'scale(1.03)' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(10,10,15,0.9) 0%, rgba(10,10,15,0.55) 55%, rgba(10,10,15,0.2) 100%)' }} />

      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 clamp(24px,6vw,100px)' }}>
        <div style={{ maxWidth: 560 }}>
          <div style={{ display: 'inline-block', padding: '6px 18px', background: aG(0.15), border: `1px solid ${aG(0.3)}`, borderRadius: 40, marginBottom: 20 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: G1 }}>Festive Collection 2026</span>
          </div>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 4vw, 3.5rem)', color: W, lineHeight: 1.1, marginBottom: 16 }}>
            Where Tradition Meets{' '}
            <em className="gold-shimmer">Brilliance</em>
          </h2>
          <p style={{ fontSize: 'clamp(14px,1.5vw,17px)', color: aW(0.65), lineHeight: 1.75, marginBottom: 36 }}>
            Discover our finest festive pieces — crafted for every celebration and milestone.
          </p>
          <a href="#collections" className="btn-gold">Explore Collection</a>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── About ─────────────────────────────────────── */
function About() {
  return (
    <section id="about" style={{ padding: 'clamp(60px,8vw,120px) clamp(20px,6vw,100px)', background: BG }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 64, alignItems: 'center' }}>

        {/* Visual */}
        <div style={{ position: 'relative' }}>
          {/* Main image */}
          <div style={{ borderRadius: 4, overflow: 'hidden', position: 'relative', border: `1px solid ${aG(0.15)}` }}>
            <img src={banner3} alt="Pushpa Chains" style={{ width: '100%', height: 420, objectFit: 'cover', objectPosition: 'center 15%', display: 'block' }} />
            {/* Overlay */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(10,10,15,0.7) 0%, transparent 50%)' }} />
            {/* Logo overlay at bottom */}
            <div style={{ position: 'absolute', bottom: 24, left: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', border: `2px solid ${aG(0.6)}`, boxShadow: `0 4px 16px ${aG(0.4)}` }}>
                <img src={logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div>
                <div style={{ fontFamily: serif, fontSize: 14, fontWeight: 700, lineHeight: 1 }} className="gold-text">Pushpa Chains</div>
                <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: aW(0.55) }}>Est. 1985</div>
              </div>
            </div>
            {/* Gold corner accent */}
            <div style={{ position: 'absolute', top: 0, right: 0, width: 80, height: 80, background: `linear-gradient(225deg, ${aG(0.15)}, transparent)`, pointerEvents: 'none' }} />
          </div>

          {/* Floating stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 14 }} className="about-stats-grid">
            {[
              { v: '1985', l: 'Est.', icon: <HistoryEduIcon sx={{ fontSize: 20, color: G1 }} /> },
              { v: '40+',  l: 'Years', icon: <StarIcon sx={{ fontSize: 20, color: G1 }} /> },
              { v: '3rd',  l: 'Generation', icon: <CelebrationIcon sx={{ fontSize: 20, color: G1 }} /> },
              { v: '10K+', l: 'Families', icon: <WorkspacePremiumIcon sx={{ fontSize: 20, color: G1 }} /> },
            ].map(({ v, l, icon }) => (
              <div key={l} className="glass-gold" style={{ padding: '16px 12px', borderRadius: 10, textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>{icon}</div>
                <div style={{ fontFamily: serif, fontSize: 22, fontWeight: 900, lineHeight: 1 }} className="gold-text">{v}</div>
                <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: aW(0.4), marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Text */}
        <div>
          <Ornament />
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: G1, marginBottom: 16 }}>Our Story</p>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 3.5vw, 3.2rem)', color: W, lineHeight: 1.1, marginBottom: 28 }}>
            A Legacy Forged in{' '}
            <em className="gold-shimmer">Gold & Trust</em>
          </h2>
          <p style={{ fontSize: 17, color: aW(0.65), lineHeight: 1.9, marginBottom: 20 }}>
            Founded in 1985 by our grandfather in Nagarathpete — Bengaluru's historic jewellery district — Pushpa Chains began as a small karigar workshop. Three generations later, we are one of the city's most trusted jewellery manufacturers.
          </p>
          <p style={{ fontSize: 17, color: aW(0.65), lineHeight: 1.9, marginBottom: 44 }}>
            We blend age-old Indian goldsmithing with contemporary design, producing pieces that pass three rounds of quality checks before leaving our workshop.
          </p>

          {/* Badges */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {[
              { icon: <WorkspacePremiumIcon sx={{ fontSize: 17, color: G1 }} />, t: 'BIS Certified' },
              { icon: <HandymanIcon         sx={{ fontSize: 17, color: G1 }} />, t: 'Handcrafted' },
              { icon: <VerifiedIcon         sx={{ fontSize: 17, color: G1 }} />, t: '3× Quality Check' },
              { icon: <CelebrationIcon      sx={{ fontSize: 17, color: G1 }} />, t: 'Family Business' },
            ].map(({ icon, t }) => (
              <div key={t} className="glass-gold" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderRadius: 8 }}>
                {icon}
                <span style={{ fontSize: 13, fontWeight: 600, color: aW(0.8) }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Why Us ────────────────────────────────────── */
function WhyUs() {
  const [hov, setHov] = useState(null);
  return (
    <section id="why" style={{ padding: 'clamp(60px,8vw,120px) clamp(20px,6vw,100px)', background: BG3 }}>
      <SH eyebrow="Why Choose Us" title="The Pushpa Chains Promise" sub="Six reasons families across Bengaluru have trusted us for four decades." />

      {/* Row 1 — top 3 cards */}
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {[WHY.slice(0, 3), WHY.slice(3, 6)].map((row, rowIdx) => (
          <div key={rowIdx} style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24,
            marginBottom: rowIdx === 0 ? 24 : 0,
          }}
          className="why-grid-row">
            {row.map((w, i) => {
              const globalIdx = rowIdx * 3 + i;
              const isHov = hov === globalIdx;
              return (
                <div
                  key={w.t}
                  onMouseEnter={() => setHov(globalIdx)}
                  onMouseLeave={() => setHov(null)}
                  style={{
                    position: 'relative',
                    padding: '40px 36px 36px',
                    background: isHov
                      ? `linear-gradient(145deg, ${aG(0.14)} 0%, ${BG2} 100%)`
                      : BG2,
                    border: `1px solid ${isHov ? aG(0.45) : aG(0.1)}`,
                    borderRadius: 12,
                    overflow: 'hidden',
                    transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                    transform: isHov ? 'translateY(-8px)' : 'none',
                    boxShadow: isHov
                      ? `0 28px 60px rgba(0,0,0,0.55), 0 0 48px ${aG(0.12)}`
                      : '0 4px 24px rgba(0,0,0,0.3)',
                    cursor: 'default',
                  }}>

                  {/* Top glow on hover */}
                  {isHov && (
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                      background: `linear-gradient(90deg, transparent, ${G1}, transparent)`,
                      borderRadius: '12px 12px 0 0',
                    }} />
                  )}

                  {/* Background number watermark */}
                  <div style={{
                    position: 'absolute', top: 12, right: 20,
                    fontFamily: serif, fontSize: 72, fontWeight: 900,
                    color: isHov ? aG(0.08) : aG(0.04),
                    lineHeight: 1, userSelect: 'none',
                    transition: 'color 0.35s',
                  }}>{w.n}</div>

                  {/* Icon container */}
                  <div style={{
                    width: 64, height: 64, borderRadius: 16,
                    background: isHov
                      ? `linear-gradient(135deg, ${aG(0.22)} 0%, ${aG(0.08)} 100%)`
                      : `linear-gradient(135deg, ${aG(0.12)} 0%, ${aG(0.04)} 100%)`,
                    border: `1.5px solid ${isHov ? aG(0.5) : aG(0.18)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 24, flexShrink: 0,
                    boxShadow: isHov ? `0 0 24px ${aG(0.2)}` : 'none',
                    transition: 'all 0.35s',
                  }}>
                    {WHY_ICONS[w.iconKey]}
                  </div>

                  {/* Gold accent line */}
                  <div style={{
                    width: isHov ? 56 : 36, height: 2,
                    background: `linear-gradient(90deg, ${G1}, transparent)`,
                    marginBottom: 16, transition: 'width 0.35s',
                    borderRadius: 2,
                  }} />

                  <h3 style={{
                    fontFamily: serif, fontSize: 20, fontWeight: 700,
                    color: isHov ? G2 : W, marginBottom: 12,
                    transition: 'color 0.35s',
                    lineHeight: 1.3,
                  }}>{w.t}</h3>

                  <p style={{ fontSize: 14, color: aW(isHov ? 0.65 : 0.48), lineHeight: 1.85, transition: 'color 0.35s' }}>
                    {w.d}
                  </p>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <style>{`
        @media (max-width: 900px) {
          .why-grid-row {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 580px) {
          .why-grid-row {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 640px) {
          .about-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 380px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}

/* ─────────────────────────── Reviews ───────────────────────────────────── */
function Reviews() {
  return (
    <section id="reviews" style={{ padding: 'clamp(60px,8vw,120px) clamp(20px,6vw,100px)', background: BG }}>
      <SH eyebrow="Customer Stories" title="Trusted by Thousands" sub="Hear from the families who made Pushpa Chains part of their most cherished moments." />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, maxWidth: 1280, margin: '0 auto 80px' }}>
        {REVIEWS.map((r) => (
          <div key={r.n} className="glass lift" style={{ padding: 36, borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
            {/* Huge quote */}
            <div style={{ position: 'absolute', top: 10, right: 20, fontFamily: 'Georgia', fontSize: 100, color: aG(0.07), lineHeight: 1, userSelect: 'none', fontWeight: 900 }}>"</div>

            {/* Stars */}
            <div style={{ display: 'flex', gap: 3, marginBottom: 20 }}>
              {[...Array(5)].map((_, i) => (
                <span key={i} style={{ color: G1, fontSize: 15 }}>★</span>
              ))}
            </div>

            <p style={{ fontSize: 15, color: aW(0.72), lineHeight: 1.85, marginBottom: 28, fontStyle: 'italic', position: 'relative', zIndex: 1 }}>"{r.t}"</p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg, ${G3}, ${G1})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, color: BG, flexShrink: 0 }}>
                {r.n.charAt(0)}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: W }}>{r.n}</div>
                <div style={{ fontSize: 12, color: aW(0.35) }}>{r.l}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Counter row — premium stats banner */}
      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
        {/* Outer glow */}
        <div style={{ position: 'absolute', inset: -1, borderRadius: 20, background: `linear-gradient(135deg, ${aG(0.35)}, ${aG(0.05)}, ${aG(0.25)})`, zIndex: 0 }} />
        <div style={{
          position: 'relative', zIndex: 1,
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          background: `linear-gradient(135deg, ${BG2} 0%, #16162A 50%, ${BG2} 100%)`,
          borderRadius: 18, overflow: 'hidden',
        }} className="stats-grid">

          {[
            { v: '40+',     l: 'Years of Excellence', icon: <HistoryEduIcon sx={{ fontSize: 28, color: G1 }} />,         sub: 'Est. 1985' },
            { v: '10,000+', l: 'Happy Families',       icon: <CelebrationIcon sx={{ fontSize: 28, color: G1 }} />,        sub: 'Across Karnataka' },
            { v: '50,000+', l: 'Pieces Crafted',       icon: <AutoAwesomeIcon sx={{ fontSize: 28, color: G1 }} />,        sub: 'Handcrafted with care' },
            { v: '4.9',     l: 'Average Rating',        icon: <WorkspacePremiumIcon sx={{ fontSize: 28, color: G1 }} />,  sub: '★★★★★ Reviews' },
          ].map(({ v, l, icon, sub }, idx) => (
            <div key={l} style={{ position: 'relative', padding: 'clamp(28px,4vw,48px) clamp(16px,2vw,32px)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>

              {/* Vertical divider (not on last) */}
              {idx < 3 && (
                <div style={{ position: 'absolute', right: 0, top: '15%', bottom: '15%', width: 1, background: `linear-gradient(180deg, transparent, ${aG(0.3)}, transparent)` }} />
              )}

              {/* Icon circle */}
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: `radial-gradient(circle at 35% 35%, ${aG(0.22)}, ${aG(0.06)})`,
                border: `1.5px solid ${aG(0.35)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 0 20px ${aG(0.15)}`,
                marginBottom: 4,
              }}>
                {icon}
              </div>

              {/* Value */}
              <div style={{
                fontFamily: serif,
                fontSize: 'clamp(2rem, 3.5vw, 3.2rem)',
                fontWeight: 900, lineHeight: 1,
                background: `linear-gradient(135deg, ${G2} 0%, ${G1} 55%, ${G3} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>{v}</div>

              {/* Label */}
              <div style={{ fontSize: 13, fontWeight: 700, color: W, letterSpacing: '0.04em' }}>{l}</div>

              {/* Sub label */}
              <div style={{ fontSize: 11, color: aW(0.35), letterSpacing: '0.06em' }}>{sub}</div>

              {/* Bottom gold line accent */}
              <div style={{ width: 32, height: 2, borderRadius: 2, background: `linear-gradient(90deg, transparent, ${G1}, transparent)`, marginTop: 4 }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Enquiry Form ───────────────────────────────── */
function EnquiryForm() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', interest: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const interests = [
    { v: '', label: 'Select your interest' },
    { v: 'gold-chains',   label: 'Gold Chains' },
    { v: 'necklace-sets', label: 'Necklace Sets' },
    { v: 'bangles',       label: 'Bangles & Kadas' },
    { v: 'diamond',       label: 'Diamond Jewellery' },
    { v: 'silver',        label: 'Silver Articles' },
    { v: 'custom',        label: 'Custom Order' },
    { v: 'bridal',        label: 'Bridal Collection' },
    { v: 'other',         label: 'Other / Just Browsing' },
  ];

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = encodeURIComponent(
      `Hi Pushpa Chains! I'd like to enquire.\n\nName: ${form.name}\nPhone: ${form.phone}${form.email ? '\nEmail: ' + form.email : ''}\nInterest: ${form.interest || 'General'}\n${form.message ? 'Message: ' + form.message : ''}`
    );
    window.open(`https://wa.me/917349341733?text=${msg}`, '_blank');
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setForm({ name: '', phone: '', email: '', interest: '', message: '' });
  };

  const fieldStyle = (name) => ({
    width: '100%',
    padding: '14px 16px 14px 48px',
    background: focusedField === name ? `rgba(212,175,55,0.06)` : BG,
    border: `1.5px solid ${focusedField === name ? G1 : aG(0.18)}`,
    borderRadius: 10,
    color: W,
    fontSize: 15,
    fontFamily: sans,
    outline: 'none',
    transition: 'all 0.25s',
    boxShadow: focusedField === name ? `0 0 0 3px ${aG(0.1)}` : 'none',
  });

  const labelStyle = {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: G1,
    marginBottom: 8,
  };

  const iconWrap = {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
    color: aW(0.3),
  };

  return (
    <section style={{ background: BG2, padding: 'clamp(60px,8vw,120px) clamp(20px,6vw,100px)', position: 'relative', overflow: 'hidden' }}>

      {/* Background glow blobs */}
      <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${aG(0.06)} 0%, transparent 65%)`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -80, left: -80, width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle, ${aG(0.04)} 0%, transparent 65%)`, pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1280, margin: '0 auto' }}>

        {/* Section heading */}
        <SH eyebrow="Get In Touch" title="Send Us an Enquiry" sub="Fill in your details and we'll get back to you on WhatsApp within minutes." />

        {/* Two-column layout: image + form */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 0, border: `1px solid ${aG(0.14)}`, borderRadius: 16, overflow: 'hidden', boxShadow: `0 40px 100px rgba(0,0,0,0.6), 0 0 60px ${aG(0.05)}` }}
          className="enquiry-grid">

          {/* LEFT — image panel */}
          <div style={{ position: 'relative', minHeight: 460, overflow: 'hidden' }}>
            <img
              src={banner1}
              alt="Pushpa Chains Store"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 20%', display: 'block', minHeight: 460 }}
            />
            {/* Dark gradient overlay */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(10,10,15,0.3) 0%, rgba(10,10,15,0.75) 100%)' }} />

            {/* Overlay content */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: 'clamp(24px,4vw,44px)' }}>
              {/* Logo badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', border: `2px solid ${aG(0.6)}`, boxShadow: `0 0 20px ${aG(0.35)}`, flexShrink: 0 }}>
                  <img src={logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div>
                  <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 700 }} className="gold-shimmer">Pushpa Chains</div>
                  <div style={{ fontSize: 10, letterSpacing: '0.13em', textTransform: 'uppercase', color: aW(0.5), marginTop: 2 }}>Fine Jewellery · Est. 1985</div>
                </div>
              </div>

              <h3 style={{ fontFamily: serif, fontSize: 'clamp(22px,2.8vw,30px)', color: W, lineHeight: 1.25, marginBottom: 12 }}>
                Crafted with <em className="gold-text">Passion</em>,<br/>Trusted for <em className="gold-text">Decades</em>
              </h3>
              <p style={{ fontSize: 14, color: aW(0.6), lineHeight: 1.8, marginBottom: 28, maxWidth: 360 }}>
                Walk into our store in Nagarathpete or send us an enquiry — our jewellery experts are ready to help you find the perfect piece.
              </p>

              {/* Quick info pills */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {[
                  { icon: <StorefrontIcon sx={{ fontSize: 14 }} />, text: 'Open Every Day' },
                  { icon: <DiamondIcon sx={{ fontSize: 14 }} />, text: 'BIS Hallmarked' },
                  { icon: <VerifiedIcon sx={{ fontSize: 14 }} />, text: '40+ Years Trust' },
                ].map((pill) => (
                  <div key={pill.text} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 40,
                    background: aG(0.15), border: `1px solid ${aG(0.35)}`,
                    backdropFilter: 'blur(8px)', color: G1,
                    fontSize: 12, fontWeight: 600,
                  }}>
                    {pill.icon}
                    {pill.text}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — form */}
          <div style={{ padding: 'clamp(32px,5vw,52px)', background: BG3, position: 'relative' }}>

            {/* Top glow line */}
            <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 2, background: `linear-gradient(90deg, transparent, ${G1}, transparent)`, borderRadius: 2 }} />

            {submitted ? (
              /* ── Success state ── */
              <div style={{ height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 20 }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: aG(0.15), border: `2px solid ${aG(0.5)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'glow-pulse 2s ease-in-out infinite' }}>
                  <CheckCircleIcon sx={{ fontSize: 40, color: G1 }} />
                </div>
                <h3 style={{ fontFamily: serif, fontSize: 26, color: W }}>
                  Enquiry <em className="gold-text">Sent!</em>
                </h3>
                <p style={{ fontSize: 14, color: aW(0.55), lineHeight: 1.8, maxWidth: 300 }}>
                  Your message has been sent via WhatsApp. We'll get back to you shortly.
                </p>
              </div>
            ) : (
              /* ── Form ── */
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                <div>
                  <div style={{ fontFamily: serif, fontSize: 'clamp(20px,2.2vw,26px)', color: W, fontWeight: 700, lineHeight: 1.2, marginBottom: 6 }}>
                    Send Your <em className="gold-text">Enquiry</em>
                  </div>
                  <p style={{ fontSize: 13, color: aW(0.4), lineHeight: 1.6 }}>
                    We reply on WhatsApp within minutes.
                  </p>
                </div>

                {/* Row: name + phone */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="form-two-col">
                  {/* Name */}
                  <div>
                    <label style={labelStyle}>Full Name *</label>
                    <div style={{ position: 'relative' }}>
                      <div style={iconWrap}><PersonIcon sx={{ fontSize: 18 }} /></div>
                      <input
                        type="text" name="name" required placeholder="Your name"
                        value={form.name} onChange={handleChange}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        style={fieldStyle('name')}
                      />
                    </div>
                  </div>
                  {/* Phone */}
                  <div>
                    <label style={labelStyle}>Phone Number *</label>
                    <div style={{ position: 'relative' }}>
                      <div style={iconWrap}><PhoneIcon sx={{ fontSize: 18 }} /></div>
                      <input
                        type="tel" name="phone" required placeholder="+91 XXXXX XXXXX"
                        value={form.phone} onChange={handleChange}
                        onFocus={() => setFocusedField('phone')}
                        onBlur={() => setFocusedField(null)}
                        style={fieldStyle('phone')}
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label style={labelStyle}>Email Address</label>
                  <div style={{ position: 'relative' }}>
                    <div style={iconWrap}><EmailIcon sx={{ fontSize: 18 }} /></div>
                    <input
                      type="email" name="email" placeholder="your@email.com (optional)"
                      value={form.email} onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      style={fieldStyle('email')}
                    />
                  </div>
                </div>

                {/* Interest */}
                <div>
                  <label style={labelStyle}>I'm Interested In *</label>
                  <div style={{ position: 'relative' }}>
                    <div style={{ ...iconWrap }}><DiamondIcon sx={{ fontSize: 18 }} /></div>
                    <select
                      name="interest" required
                      value={form.interest} onChange={handleChange}
                      onFocus={() => setFocusedField('interest')}
                      onBlur={() => setFocusedField(null)}
                      style={{ ...fieldStyle('interest'), appearance: 'none', cursor: 'pointer',
                        color: form.interest ? W : aW(0.35),
                        paddingRight: 40,
                      }}>
                      {interests.map((opt) => (
                        <option key={opt.v} value={opt.v} style={{ background: BG2, color: W }}>{opt.label}</option>
                      ))}
                    </select>
                    <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: aW(0.4) }}>
                      <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label style={labelStyle}>Message</label>
                  <div style={{ position: 'relative' }}>
                    <div style={{ ...iconWrap, top: 16, transform: 'none' }}><ChatBubbleOutlineIcon sx={{ fontSize: 18 }} /></div>
                    <textarea
                      name="message" rows={3}
                      placeholder="Tell us what you're looking for, your budget, occasion…"
                      value={form.message} onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      style={{ ...fieldStyle('message'), resize: 'vertical', minHeight: 90, paddingTop: 14, lineHeight: 1.6 }}
                    />
                  </div>
                </div>

                {/* Submit */}
                <button type="submit" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '16px 28px',
                  background: `linear-gradient(135deg, ${G2} 0%, ${G1} 50%, ${G3} 100%)`,
                  color: BG, border: 'none', borderRadius: 10,
                  fontFamily: sans, fontSize: 15, fontWeight: 800,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  cursor: 'pointer', transition: 'all 0.3s',
                  boxShadow: `0 8px 28px ${aG(0.35)}`,
                  position: 'relative', overflow: 'hidden',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 16px 40px ${aG(0.5)}`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 8px 28px ${aG(0.35)}`; }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                  Send Enquiry via WhatsApp
                  <SendIcon sx={{ fontSize: 18 }} />
                </button>

                <p style={{ fontSize: 12, color: aW(0.3), textAlign: 'center', lineHeight: 1.6 }}>
                  By submitting, you'll be redirected to WhatsApp to send your message directly.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .form-two-col {
            grid-template-columns: 1fr !important;
          }
          .enquiry-grid {
            border-radius: 12px !important;
          }
        }
      `}</style>
    </section>
  );
}

/* ─────────────────────────── Contact ───────────────────────────────────── */
function Contact() {
  return (
    <section id="contact" style={{ background: BG, padding: 'clamp(60px,8vw,120px) 0', overflow: 'hidden', position: 'relative' }}>

      {/* Background decorative rings */}
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 900, height: 900, borderRadius: '50%', border: `1px solid ${aG(0.04)}`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 600, borderRadius: '50%', border: `1px solid ${aG(0.06)}`, pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,6vw,80px)' }}>
        <SH eyebrow="Come See Us" title="Visit Our Store" sub="We're open every day at Bengaluru's oldest jewellery market. Walk in and let us find your perfect piece." />

        {/* ── Top: 3 quick-action tiles ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 16, marginBottom: 40 }}>
          {[
            {
              icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a8.5 8.5 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z" fill="#25D366"/><path d="M12.05 2C6.495 2 1.16 7.335 1.157 13.892c0 2.096.547 4.142 1.588 5.945L1.057 26l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0021.463 5.54 11.815 11.815 0 0012.05 2Z" stroke="#25D366" strokeWidth="1" fill="none"/></svg>,
              label: 'Chat on WhatsApp', sub: 'Instant reply · Open 10am–10pm', href: 'https://wa.me/917349341733', c: '#25D366', cBg: '#0D1F10',
            },
            {
              icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2Z" fill={G1}/></svg>,
              label: 'Call Now', sub: '+91 73493 41733', href: 'tel:+917349341733', c: G1, cBg: '#1A1500',
            },
            {
              icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5Z" fill="#E8402A"/></svg>,
              label: 'Get Directions', sub: 'Nagarathpete, Bengaluru', href: 'https://maps.google.com/?q=15/1+Ganigara+C+Lane+Nagarathpete+Bengaluru', c: '#E8402A', cBg: '#1A0800',
            },
          ].map((tile) => (
            <a key={tile.label} href={tile.href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 20,
                padding: '28px 32px', borderRadius: 4,
                background: tile.cBg, border: `1px solid ${tile.c}22`,
                transition: 'all 0.3s', cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.border = `1px solid ${tile.c}55`; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 20px 48px rgba(0,0,0,0.5), 0 0 32px ${tile.c}22`; }}
              onMouseLeave={(e) => { e.currentTarget.style.border = `1px solid ${tile.c}22`; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ width: 56, height: 56, borderRadius: 12, background: `${tile.c}18`, border: `1px solid ${tile.c}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {tile.icon}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: W, marginBottom: 4 }}>{tile.label}</div>
                  <div style={{ fontSize: 13, color: aW(0.45) }}>{tile.sub}</div>
                </div>
                <div style={{ marginLeft: 'auto', color: aW(0.25), fontSize: 20 }}>›</div>
              </div>
            </a>
          ))}
        </div>

        {/* ── Main card ── */}
        <div style={{ border: `1px solid ${aG(0.12)}`, borderRadius: 4, overflow: 'hidden', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', boxShadow: `0 40px 100px rgba(0,0,0,0.7), 0 0 80px ${aG(0.04)}` }}>

          {/* LEFT — Store info */}
          <div style={{ padding: 'clamp(36px,5vw,56px)', background: BG2, position: 'relative', overflow: 'hidden' }}>
            {/* Glow */}
            <div style={{ position: 'absolute', top: -60, left: -60, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${aG(0.08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

            {/* Logo badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 40, position: 'relative' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', border: `2px solid ${aG(0.5)}`, boxShadow: `0 0 20px ${aG(0.3)}`, flexShrink: 0 }}>
                <img src={logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div>
                <div style={{ fontFamily: serif, fontSize: 20, fontWeight: 700, lineHeight: 1.1 }} className="gold-shimmer">Pushpa Chains</div>
                <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: aW(0.32), marginTop: 3 }}>Fine Jewellery · Est. 1985</div>
              </div>
            </div>

            {/* Info rows */}
            {[
              {
                svg: <svg width="20" height="20" viewBox="0 0 24 24" fill={G1}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5Z"/></svg>,
                label: 'Our Address',
                value: '15/1, Ganigara C Lane (Cross)',
                value2: 'Nagarathpete, Bengaluru — 560 002',
                href: 'https://maps.google.com/?q=15/1+Ganigara+C+Lane+Nagarathpete+Bengaluru',
              },
              {
                svg: <svg width="20" height="20" viewBox="0 0 24 24" fill={G1}><path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2Z"/></svg>,
                label: 'Phone Number',
                value: '+91 73493 41733',
                value2: 'Available 10:00 AM – 10:00 PM',
                href: 'tel:+917349341733',
              },
              {
                svg: <svg width="20" height="20" viewBox="0 0 24 24" fill={G1}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm.5 15H11v-6h1.5v6Zm0-8H11V7h1.5v2Z"/></svg>,
                label: 'Store Hours',
                value: 'Open Every Day',
                value2: '10:00 AM – 10:00 PM',
                href: null,
              },
            ].map((item) => (
              <a key={item.label} href={item.href || undefined} target={item.href ? '_blank' : undefined} rel="noreferrer"
                style={{ display: 'flex', gap: 18, marginBottom: 28, textDecoration: 'none', cursor: item.href ? 'pointer' : 'default' }}
                onMouseEnter={(e) => { if (item.href) e.currentTarget.querySelector('.info-val').style.color = G1; }}
                onMouseLeave={(e) => { if (item.href) e.currentTarget.querySelector('.info-val').style.color = W; }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: aG(0.1), border: `1px solid ${aG(0.2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {item.svg}
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: G1, marginBottom: 5 }}>{item.label}</div>
                  <div className="info-val" style={{ fontSize: 15, color: W, fontWeight: 600, lineHeight: 1.4, transition: 'color 0.2s' }}>{item.value}</div>
                  <div style={{ fontSize: 13, color: aW(0.42), marginTop: 2 }}>{item.value2}</div>
                </div>
              </a>
            ))}

            {/* Divider */}
            <div style={{ height: 1, background: `linear-gradient(90deg, ${aG(0.35)}, ${aG(0.08)}, transparent)`, margin: '8px 0 28px' }} />

            {/* Social heading */}
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: aW(0.28), marginBottom: 18 }}>Follow & Connect</div>

            {/* Social row — large, beautiful */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {[
                { label: 'WhatsApp', sub: 'Message us', href: 'https://wa.me/917349341733', c: '#25D366',
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg> },
                { label: 'Instagram', sub: '@pushpachains', href: '#', c: '#E1306C',
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
                { label: 'Facebook', sub: 'Pushpa Chains', href: '#', c: '#1877F2',
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
              ].map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', flex: '1 1 auto', minWidth: 90 }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                    padding: '16px 12px', borderRadius: 10,
                    background: `${s.c}12`, border: `1px solid ${s.c}28`,
                    color: s.c, transition: 'all 0.25s', textAlign: 'center',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${s.c}22`; e.currentTarget.style.border = `1px solid ${s.c}55`; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = `${s.c}12`; e.currentTarget.style.border = `1px solid ${s.c}28`; e.currentTarget.style.transform = 'none'; }}>
                    {s.icon}
                    <span style={{ fontSize: 12, fontWeight: 700, color: W }}>{s.label}</span>
                    <span style={{ fontSize: 10, color: aW(0.35) }}>{s.sub}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* RIGHT — Map + CTA */}
          <div style={{ display: 'flex', flexDirection: 'column', borderLeft: `1px solid ${aG(0.1)}` }}>
            {/* Embedded Google map placeholder (styled) */}
            <div style={{ flex: 1, minHeight: 220, position: 'relative', overflow: 'hidden', background: '#0D1520' }}>
              <iframe
                title="Pushpa Chains Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.5994!2d77.5852!3d12.9784!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1584c72f96c9%3A0x5f0d54d5b7c5e98a!2sNagarathpete%2C%20Bengaluru%2C%20Karnataka%20560002!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                width="100%" height="100%"
                style={{ border: 'none', filter: 'invert(92%) hue-rotate(180deg) brightness(0.9) saturate(0.7)', minHeight: 220, display: 'block' }}
                loading="lazy"
                allowFullScreen
              />
              {/* Pin label */}
              <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, padding: '12px 16px', background: 'rgba(10,10,15,0.92)', backdropFilter: 'blur(12px)', borderRadius: 8, border: `1px solid ${aG(0.2)}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', border: `1.5px solid ${aG(0.5)}`, flexShrink: 0 }}>
                  <img src={logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: W }}>Pushpa Chains</div>
                  <div style={{ fontSize: 11, color: aW(0.45) }}>Nagarathpete, Bengaluru</div>
                </div>
                <a href="https://maps.google.com/?q=15/1+Ganigara+C+Lane+Nagarathpete+Bengaluru" target="_blank" rel="noreferrer"
                  style={{ marginLeft: 'auto', padding: '6px 14px', background: aG(0.15), border: `1px solid ${aG(0.35)}`, borderRadius: 6, color: G1, fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                  Open Maps →
                </a>
              </div>
            </div>

            {/* CTA area */}
            <div style={{ padding: 'clamp(28px,4vw,44px)', background: BG3, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <h3 style={{ fontFamily: serif, fontSize: 'clamp(20px,2.5vw,26px)', color: W, lineHeight: 1.2, marginBottom: 10 }}>
                  Ready for Your <em className="gold-text">Perfect Piece?</em>
                </h3>
                <p style={{ fontSize: 14, color: aW(0.5), lineHeight: 1.75 }}>
                  Our experts guide you to the right jewellery for every occasion, style and budget. Walk in — no appointment needed.
                </p>
              </div>

              <a href="https://wa.me/917349341733" target="_blank" rel="noreferrer" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                padding: '15px 28px', background: '#25D366', color: '#fff', borderRadius: 4,
                textDecoration: 'none', fontWeight: 700, fontSize: 15, letterSpacing: '0.03em',
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background='#1fbd5c'; e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 12px 32px rgba(37,211,102,0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background='#25D366'; e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                Chat on WhatsApp
              </a>

              <a href="tel:+917349341733" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                padding: '14px 28px', background: 'transparent', borderRadius: 4,
                border: `1.5px solid ${aG(0.4)}`, color: G1, textDecoration: 'none',
                fontWeight: 600, fontSize: 14, letterSpacing: '0.05em', textTransform: 'uppercase',
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = aG(0.1); e.currentTarget.style.borderColor = G1; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = aG(0.4); e.currentTarget.style.transform = 'none'; }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill={G1}><path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2Z"/></svg>
                Call +91 73493 41733
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Footer ────────────────────────────────────── */
function Footer() {
  return (
    <footer style={{ background: '#07070F', borderTop: `1px solid ${aG(0.1)}`, padding: '40px clamp(20px,6vw,100px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: '50%', overflow: 'hidden', border: `2px solid ${aG(0.3)}`, flexShrink: 0 }}>
            <img src={logo} alt="Pushpa Chains" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div>
            <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, lineHeight: 1 }} className="gold-text">Pushpa Chains</div>
            <p style={{ fontSize: 10, color: aW(0.22), letterSpacing: '0.08em', marginTop: 3 }}>Fine Jewellery · Bengaluru since 1985</p>
          </div>
        </div>
        <p style={{ fontSize: 12, color: aW(0.18) }}>© {new Date().getFullYear()} Pushpa Chains. All rights reserved.</p>
        <div style={{ display: 'flex', gap: 32 }}>
          {[['Collections','#collections'],['About','#about'],['Why Us','#why'],['Contact','#contact']].map(([l, h]) => (
            <a key={l} href={h} className="nav-link" style={{ fontSize: 12 }}>{l}</a>
          ))}
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────── App ───────────────────────────────────────── */
export default function App() {
  return (
    <>
      <GlobalStyle />
      <Navbar />
      <Hero />
      <Ticker />
      <Collections />
      <BannerMid />
      <About />
      <WhyUs />
      <Reviews />
      <Contact />
      <EnquiryForm />
      <Footer />
    </>
  );
}
